/**
 * « Carnet de la semaine » : préparer, imprimer, voir ce qui est revenu.
 *
 * Le carnet est préparé le dimanche soir par le cycle quotidien — une fiche par
 * soir de classe —, ou ici à la main. Le parent imprime **deux documents** : le
 * carnet, qui se pose devant l'enfant, et le corrigé, qui reste chez l'adulte.
 * Les deux liens sont des routes du parent seulement (règle 2) ; le navigateur
 * les ouvre avec le cookie de session, et le serveur répond `no-store`.
 *
 * Chaque soir l'enfant photographie sa fiche. La photo est posée sur chaque
 * exercice de la fiche, et le jugement se fait **dans « À valider »**, ligne par
 * ligne, avec le reste de ce qui attend un parent : pas un second écran de
 * jugement qui finirait par ne plus se comporter comme le premier.
 *
 * **Carnet éteint pour cet enfant** : la route répond 404, et l'écran le dit
 * au lieu d'afficher une liste vide, qui se lirait « rien de préparé ».
 *
 * « Refaire » n'est proposé que tant qu'aucune fiche n'est revenue : un carnet
 * commencé est sur la table de l'enfant, et le serveur refuse de le remplacer.
 * La confirmation est rendue dans la page, jamais par `window.confirm` (refus
 * silencieux dans la coquille Android).
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { buildUrl, call } from "@/api/client";
import { ApiError } from "@/api/errors";
import type { CarnetOut, CarnetsOut, FicheDuCarnetOut } from "@/api/schema";
import { AsseoirACote, peutSAsseoir } from "@/ui/asseoir";
import { enClair, enClairCourt } from "@/ui/dates";
import { useEnfants } from "@/ui/enfants";
import {
  Button,
  Card,
  Empty,
  ErrorNote,
  Loading,
  Screen,
} from "@/ui/primitives";
import { entrainementsDe } from "@/v2/chemins";

/** Ce que dit une fiche de son retour, en une ligne. */
export function etatDeLaFiche(fiche: FicheDuCarnetOut): string {
  const n = (fiche.feuille.lignes ?? []).length;
  const exercices = `${n} exercice${n > 1 ? "s" : ""}`;
  if (!fiche.rendue) return `${exercices} — pas encore rendue`;
  const jugees = fiche.feuille.jugees ?? 0;
  const quand = fiche.rendue_at ? ` le ${enClair(fiche.rendue_at)}` : "";
  return `${exercices} — rendue${quand}, ${jugees} sur ${n} jugé${jugees > 1 ? "s" : ""}`;
}

/**
 * Une fiche, et sous elle ses exercices avec « M'asseoir à côté » (lot P4) : le
 * soir de la fiche, le parent peut s'asseoir à côté de l'enfant sur l'un d'eux.
 * La feuille d'une fiche est close dès sa préparation — l'enfant l'a reçue —,
 * donc seuls les exercices jugés et les dictées n'ont pas le bouton.
 */
function Fiche({ fiche, childId }: { fiche: FicheDuCarnetOut; childId: string }) {
  const close = Boolean(fiche.feuille.closed_at);
  const lignes = (fiche.feuille.lignes ?? []).filter((ligne) => peutSAsseoir(ligne, close));
  return (
    <li className="list__item list__item--pile">
      <p>
        {enClairCourt(fiche.jour)} — fiche <strong>{fiche.code}</strong>
      </p>
      <p className="muted small">{fiche.titre}</p>
      <p className="muted small">{etatDeLaFiche(fiche)}</p>
      {lignes.length ? (
        <ul className="small">
          {lignes.map((ligne) => (
            <li key={ligne.ligne_id}>
              {ligne.numero ? `Exercice ${ligne.numero}` : `Exercice ${ligne.rang}`} —{" "}
              {ligne.competence}{" "}
              <AsseoirACote childId={childId} cible={{ ligneId: ligne.ligne_id }} />
            </li>
          ))}
        </ul>
      ) : null}
    </li>
  );
}

function Refaire({ refaire }: { refaire: () => Promise<void> }) {
  const [etat, setEtat] = useState<"ferme" | "confirme" | "envoi">("ferme");
  if (etat === "ferme") {
    return (
      <Button type="button" variant="quiet" onClick={() => setEtat("confirme")}>
        Refaire
      </Button>
    );
  }
  return (
    <div className="notice">
      <p>
        Refaire ce carnet ? Les exercices sont tirés à nouveau ; un carnet déjà
        imprimé ne correspondra plus.
      </p>
      <div className="screen__actions">
        <Button
          type="button"
          disabled={etat === "envoi"}
          onClick={() => {
            setEtat("envoi");
            void refaire().catch(() => setEtat("confirme"));
          }}
        >
          {etat === "envoi" ? "Préparation…" : "Oui, refaire"}
        </Button>
        <Button type="button" variant="quiet" onClick={() => setEtat("ferme")}>
          Annuler
        </Button>
      </div>
    </div>
  );
}

function Carnet({
  childId,
  carnet,
  refaire,
}: {
  childId: string;
  carnet: CarnetOut;
  refaire: () => Promise<void>;
}) {
  const fiches = carnet.fiches ?? [];
  const params = { child_id: childId, carnet_id: carnet.id };
  return (
    <Card>
      <p className="card__title">Semaine du {enClair(carnet.semaine)}</p>
      <p className="muted small">
        {(carnet.matieres ?? []).join(", ") || "aucune matière"} —{" "}
        {carnet.rendues} fiche
        {carnet.rendues > 1 ? "s" : ""} rendue{carnet.rendues > 1 ? "s" : ""}{" "}
        sur {fiches.length}
      </p>
      {/* Deux documents, jamais un seul : le carnet devant l'enfant, le
          corrigé chez l'adulte. */}
      <div className="screen__actions">
        <a
          className="btn btn--quiet"
          href={buildUrl("imprimerLeCarnet", { params })}
          target="_blank"
          rel="noreferrer"
        >
          Imprimer le carnet
        </a>
        <a
          className="btn btn--quiet"
          href={buildUrl("imprimerLeCorrigeDuCarnet", { params })}
          target="_blank"
          rel="noreferrer"
        >
          Imprimer le corrigé
        </a>
      </div>
      <ul className="list">
        {fiches.map((fiche) => (
          <Fiche key={fiche.id} fiche={fiche} childId={childId} />
        ))}
      </ul>
      {carnet.rendues > 0 ? (
        <p className="muted small">
          <Link to={`${entrainementsDe(childId)}/a-valider`}>
            Juger les fiches rendues
          </Link>
        </p>
      ) : (
        <Refaire refaire={refaire} />
      )}
    </Card>
  );
}

export function CarnetScreen() {
  const { childId = "" } = useParams();
  const enfants = useEnfants();
  const prenom = enfants.find(
    (enfant) => enfant.child_id === childId,
  )?.display_name;
  const [lu, setLu] = useState<CarnetsOut | null>(null);
  const [eteint, setEteint] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [envoi, setEnvoi] = useState(false);
  const [tour, setTour] = useState(0);

  const charger = useCallback(
    () => call("carnetsDeLEnfant", { params: { child_id: childId } }),
    [childId],
  );

  useEffect(() => {
    let annule = false;
    setErreur(null);
    void (async () => {
      try {
        const reponse = await charger();
        if (!annule) {
          setLu(reponse);
          setEteint(false);
        }
      } catch (echec) {
        if (annule) return;
        if (echec instanceof ApiError && echec.status === 404) setEteint(true);
        else
          setErreur(
            echec instanceof ApiError
              ? echec.message
              : "Chargement impossible.",
          );
      }
    })();
    return () => {
      annule = true;
    };
  }, [charger, tour]);

  const preparer = async (semaine: string, refaire: boolean) => {
    setErreur(null);
    try {
      await call("preparerLeCarnet", {
        params: { child_id: childId },
        body: { semaine, refaire },
      });
      setTour((avant) => avant + 1);
    } catch (echec) {
      setErreur(
        echec instanceof ApiError ? echec.message : "La préparation a échoué.",
      );
      throw echec;
    }
  };

  const carnets = lu?.carnets ?? [];
  const aPreparer = lu?.semaine_a_preparer;
  const dejaPrepare = carnets.some((carnet) => carnet.semaine === aPreparer);

  return (
    <Screen
      title="Carnet de la semaine"
      lead={
        prenom
          ? `Une fiche par soir de classe, à imprimer : ${prenom} travaille sur papier et photographie sa fiche le soir`
          : undefined
      }
    >
      {erreur ? <ErrorNote>{erreur}</ErrorNote> : null}
      {eteint ? (
        <Card>
          <Empty>Le carnet papier n'est pas activé pour cet enfant.</Empty>
        </Card>
      ) : null}
      {!lu && !eteint && !erreur ? <Loading /> : null}

      {lu && aPreparer && !dejaPrepare ? (
        <Card>
          <p className="muted small">
            Le carnet se prépare seul le dimanche soir. Pour le préparer
            maintenant :
          </p>
          <Button
            type="button"
            disabled={envoi}
            onClick={() => {
              setEnvoi(true);
              void preparer(aPreparer, false)
                .catch(() => undefined)
                .finally(() => setEnvoi(false));
            }}
          >
            {envoi
              ? "Préparation…"
              : `Préparer la semaine du ${enClair(aPreparer)}`}
          </Button>
        </Card>
      ) : null}

      {lu && carnets.length === 0 ? (
        <Card>
          <Empty>Aucun carnet préparé pour l'instant.</Empty>
        </Card>
      ) : null}
      {carnets.map((carnet) => (
        <Carnet
          key={carnet.id}
          childId={childId}
          carnet={carnet}
          refaire={() => preparer(carnet.semaine, true)}
        />
      ))}
    </Screen>
  );
}
